/**
 * The 360 Family — the System of Proof and its two delivery arms.
 * EKSELENS360 (function methodology, twelve specialist lenses) + Enterprise360
 * (value-stream methodology, the reinvention engine). Built strictly on the
 * existing `ek-` design tokens.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Cpu, MonitorSmartphone, Users, GitBranch,
  ServerCog, LineChart, Target, ShieldCheck, GraduationCap, MessagesSquare,
  Workflow, GanttChartSquare,
} from "lucide-react";
import Lifecycle from "../components/Lifecycle";
import StarMotif from "../components/StarMotif";
import { LENSES, ENGINE, liteRoute, FAMILY_COUNT } from "../data/family360";
import { SERVICE_LINE_ICONS } from "../components/DeliveryIcons";

const ICONS = {
  Cpu, MonitorSmartphone, Users, GitBranch, ServerCog, LineChart, Target,
  ShieldCheck, GraduationCap, MessagesSquare, Workflow, GanttChartSquare,
};

export default function The360Family() {
  return (
    <div data-testid="family360-page">
      {/* Hero */}
      <section className="relative ek-gradient-dark text-white overflow-hidden" data-testid="family360-hero">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA] ek-fade-up">The 360 family · {FAMILY_COUNT} toolsets</div>
          <h1 className="ek-heading text-5xl md:text-6xl lg:text-7xl text-white mt-5 max-w-4xl ek-fade-up" style={{ animationDelay: "60ms" }} data-testid="family360-headline">
            Twelve lenses. Twelve blueprints. <span className="text-[#A179FA]">One system of proof.</span>
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-7 max-w-2xl ek-fade-up" style={{ animationDelay: "120ms" }}>
            The System of Proof is our evidence discipline. It’s delivered through two
            methodologies: EKSELENS360 — the function view, with twelve specialist lenses — and
            ENTERPRISE360, the value-stream view, the reinvention engine that assembles them into
            one tailored blueprint. This is how transformation should be done.
          </p>
          <div className="flex flex-wrap gap-4 mt-9 ek-fade-up" style={{ animationDelay: "180ms" }}>
            <Link to={ENGINE.path} className="ek-btn ek-btn-primary" data-testid="family360-engine-cta">
              Explore ENTERPRISE360 <ArrowRight size={16} />
            </Link>
            <Link to="/methodology" className="ek-btn ek-btn-outline-light" data-testid="family360-method-cta">
              How the method works
            </Link>
            <Link to="/how-it-works" className="ek-btn ek-btn-outline-light" data-testid="family360-how-it-works-cta">
              How it all fits together
            </Link>
            <Link to="/contact" className="ek-btn ek-btn-outline-light" data-testid="family360-contact-cta">
              Book a discovery conversation
            </Link>
          </div>
        </div>
      </section>

      {/* Lifecycle */}
      <section className="bg-white ek-section" data-testid="family360-lifecycle-section">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="ek-eyebrow">The EKSELENS360 lifecycle</div>
              <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">Discover → Optimise.<br /> One integrated method.</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="ek-body text-lg max-w-2xl">
                Every engagement follows the same disciplined arc — evidence-led, AI-ready by
                design, and obsessed with benefits actually being realised. The toolsets below
                plug into this lifecycle wherever they add the most value.
              </p>
            </div>
          </div>
          <Lifecycle />
        </div>
      </section>

      {/* The engine */}
      <section className="ek-gradient-dark-soft text-white ek-section relative overflow-hidden" data-testid="family360-engine-section">
        <div className="ek-star-watermark-left" />
        <div className="ek-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="ek-eyebrow text-[#A179FA]">Enterprise360 · the value-stream methodology</div>
              <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-white">ENTERPRISE360</h2>
              <p className="text-zinc-300 font-light text-lg leading-relaxed mt-5 max-w-2xl">
                {ENGINE.blurb} As the value-stream arm of the System of Proof, it pulls EKSELENS360’s
                twelve function lenses in as specialist deep-dives — so the whole enterprise gets
                reinvented function by function, not capability by capability.
              </p>
              <p className="text-zinc-400 font-light text-[15px] leading-relaxed mt-4 max-w-2xl">
                The twelve lenses assess <strong className="text-white">functions</strong>;
                ENTERPRISE360 assesses end-to-end <strong className="text-white">value streams</strong>.
                Its free <strong className="text-white">ValueStream360</strong> diagnostic is the place to start —
                pick one value stream and see how it really runs.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <span className="text-[13px] bg-white/10 text-white rounded-full px-4 py-1.5" data-testid="family360-stat-sectors">{ENGINE.sectors} sectors</span>
                <span className="text-[13px] bg-white/10 text-white rounded-full px-4 py-1.5" data-testid="family360-stat-archetypes">{ENGINE.archetypes} business archetypes</span>
                <span className="text-[13px] bg-white/10 text-white rounded-full px-4 py-1.5">Editable L1–L4 process maps</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/value-stream-assessment" className="ek-btn ek-btn-primary inline-flex" data-testid="family360-vs-cta">
                  Assess a value stream — free <ArrowRight size={16} />
                </Link>
                <Link to={ENGINE.path} className="ek-btn ek-btn-outline-light inline-flex" data-testid="family360-engine-explore">
                  How the engine works <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-white/15 bg-white/[0.04] p-8 relative">
                <Sparkles size={20} className="text-[#A179FA]" />
                <div className="text-[11px] font-semibold tracking-[0.18em] text-[#A179FA] uppercase mt-4">Classify · Overlay · Assemble</div>
                <p className="text-zinc-300 font-light text-[15px] mt-3">
                  Archetype recipe → sector & complexity overlays → a function-by-function
                  Reinvention Kit your board can fund and your team can deliver.
                </p>
                <div className="mt-6 grid grid-cols-3 gap-2 text-center">
                  {["Archetype", "Overlays", "Blueprint"].map((s, i) => (
                    <div key={s} className="border border-white/10 py-3">
                      <div className="text-[#A179FA] text-lg font-semibold">{i + 1}</div>
                      <div className="text-[11px] text-zinc-400 uppercase tracking-[0.12em] mt-1">{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The 12 lenses */}
      <section className="bg-[#F2F2F3] ek-section" data-testid="family360-lenses-section">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-7">
              <div className="ek-eyebrow">The twelve specialist lenses</div>
              <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">One lens per capability.</h2>
            </div>
            <div className="lg:col-span-5 flex lg:items-end">
              <p className="ek-body text-lg">
                Use one on its own or several together. Every lens has a <strong>free, no-login
                lite self-assessment</strong> — see where you stand in three minutes.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
            {LENSES.map((lens) => {
              const Icon = SERVICE_LINE_ICONS[lens.code] || (ICONS[lens.icon] || Sparkles);
              return (
                <div key={lens.code} className="ek-card block group flex flex-col" data-testid={`lens-card-${lens.liteSlug}`}>
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6] group-hover:bg-[#7737F6] group-hover:text-white transition-colors">
                      <Icon size={20} strokeWidth={1.5} className="w-5 h-5" />
                    </div>
                    <StarMotif size={16} color="#7737F6" className="opacity-60 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-[11px] font-bold tracking-[0.18em] text-[#7737F6] uppercase">{lens.code}</div>
                  <h3 className="ek-heading text-xl text-black leading-snug mt-1.5">{lens.name}</h3>
                  <p className="ek-body text-[14px] mt-2 flex-1">{lens.tagline}</p>
                  <div className="mt-5 flex items-center gap-4 border-t border-zinc-200 pt-4">
                    <Link to={`/services/${lens.serviceSlug}`} className="inline-flex items-center gap-1.5 text-[#7737F6] text-[13px] font-medium hover:underline" data-testid={`lens-service-${lens.liteSlug}`}>
                      Service <ArrowRight size={13} />
                    </Link>
                    <Link to={liteRoute(lens.liteSlug)} className="inline-flex items-center gap-1.5 text-zinc-600 text-[13px] font-medium hover:text-[#7737F6]" data-testid={`lens-lite-${lens.liteSlug}`}>
                      <Sparkles size={13} /> Free lite assessment
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-zinc-500 text-[13px] mt-6 text-center">Free lite assessments · no sign-in required.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white ek-section" data-testid="family360-cta-section">
        <div className="ek-container text-center max-w-3xl">
          <h2 className="ek-heading text-4xl md:text-5xl text-white">We are changing how transformation is done.</h2>
          <p className="text-zinc-300 font-light text-lg mt-5">
            Start with a free lite assessment, commission a full lens, or let ENTERPRISE360
            assemble the whole blueprint. One discipline, two methodologies, one system of proof.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <Link to="/contact" className="ek-btn ek-btn-primary" data-testid="family360-final-contact">
              Book a discovery conversation <ArrowRight size={16} />
            </Link>
            <Link to={ENGINE.path} className="ek-btn ek-btn-outline-light" data-testid="family360-final-engine">
              Explore ENTERPRISE360
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
